// /communities/:slug/layout — editor layout tab. Manual overrides on
// top of the auto-composed community paper (post_type → section,
// engagement → order). Standing overrides — persist until the editor
// clears them.
//
// Four primitives per post:
//   feature   hero  → full-width top card
//             hidden → drop from paper (still Live)
//   section   free text — overrides derived section
//   column    left/right — pair two consecutive posts side-by-side
//   rank      manual sort within section
//
// Renders a table with dropdowns per row + a reset button. Drag-drop
// is a v2 — this is the primitive-plumbing pass.

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { timeAgo } from '../lib/format';
import type { Post, PostLayout } from '../types/api';

interface LayoutResp { layouts: Array<PostLayout & { post_id: string }> }

// Preset section names offered in the dropdown — free text still
// allowed so a curator can invent one-off sections.
const SECTION_PRESETS = ['Tonight', 'This Week', 'Notices', 'Around the Suburb'];

export function CommunityLayout() {
    const { slug } = useParams<{ slug: string }>();
    const api = useApi();
    const { authed, login } = useAuth();
    const [posts, setPosts]     = useState<Post[]>([]);
    const [layouts, setLayouts] = useState<Record<string, PostLayout>>({});
    const [err, setErr]         = useState('');
    const [pending, setPending] = useState<Record<string, boolean>>({});

    const load = useCallback(async () => {
        if (!authed || !slug) return;
        try {
            const communities = await api.get<{ communities: { id: string; slug: string }[] }>('/api/communities');
            const c = (communities.communities || []).find(x => x.slug === slug);
            if (!c) throw new Error('community not found');
            const [postsRes, layoutRes] = await Promise.all([
                api.get<{ posts: Post[] }>('/api/posts?community_id=' + c.id + '&limit=100'),
                api.get<LayoutResp>('/api/communities/' + slug + '/layout'),
            ]);
            setPosts(postsRes.posts ?? []);
            const map: Record<string, PostLayout> = {};
            for (const l of layoutRes.layouts ?? []) {
                map[l.post_id] = {
                    paper_feature: l.paper_feature ?? null,
                    paper_section: l.paper_section ?? null,
                    paper_column:  l.paper_column  ?? null,
                    paper_rank:    l.paper_rank    ?? null,
                };
            }
            setLayouts(map);
        } catch (e) { setErr((e as Error).message); }
    }, [api, authed, slug]);
    useEffect(() => { load(); }, [load]);

    async function patch(postID: string, body: Partial<PostLayout>) {
        setPending(s => ({ ...s, [postID]: true }));
        try {
            await api.patch(`/api/communities/${slug}/posts/${postID}/layout`, body);
            setLayouts(m => ({ ...m, [postID]: { ...(m[postID] ?? {}), ...body } }));
        } catch (e) {
            setErr((e as Error).message);
        } finally {
            setPending(s => { const n = { ...s }; delete n[postID]; return n; });
        }
    }

    async function resetAll() {
        if (!confirm('Clear every layout override in this community?')) return;
        try {
            await api.post(`/api/communities/${slug}/layout/reset`, {});
            setLayouts({});
        } catch (e) { setErr((e as Error).message); }
    }

    if (!authed) return (
        <div className="prelogin">
            <p>Log in to edit layout.</p>
            <button type="button" className="plain" onClick={login}>Log in</button>
        </div>
    );
    if (err) return <main style={{ padding: 40 }}><p style={{ color: '#8b1919' }}>{err}</p></main>;

    return (
        <main style={{ padding: '20px 24px 60px', maxWidth: 1200, margin: '0 auto' }}>
            <div className="masthead" style={{ marginBottom: 12 }}>
                <div className="rule-top" />
                <h1>Layout · {slug}</h1>
                <div className="issue">Manual overrides on top of auto-composition</div>
                <div className="rule-bottom" />
            </div>
            <nav style={navStyle}>
                <Link to={`/communities/${slug}`}           style={navLink}>← Read paper</Link>
                <Link to={`/communities/${slug}/dashboard`} style={navLink}>Dashboard (stats)</Link>
                <Link to={`/communities/${slug}/newsroom`}  style={navLink}>Newsroom</Link>
                <Link to={`/communities/${slug}/layout`}    style={{ ...navLink, fontWeight: 700 }}>Layout</Link>
                <Link to={`/communities/${slug}/settings`}  style={navLink}>Settings</Link>
                <button type="button" className="plain" onClick={resetAll}
                    style={{ marginLeft: 'auto' }} title="Clear every override">
                    Reset overrides
                </button>
            </nav>

            <p style={{ fontSize: 12, color: '#666', margin: '0 0 12px' }}>
                Feature <strong>hero</strong> = full-width top of paper.
                Feature <strong>hidden</strong> = drop from paper.
                Column <strong>left</strong>/<strong>right</strong> pairs two consecutive posts side-by-side.
                Section overrides the auto-derived bucket.
            </p>

            {posts.length === 0 ? (
                <p style={{ padding: 40, color: '#666', textAlign: 'center' }}>
                    No posts in this community yet.
                </p>
            ) : (
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Post</th>
                            <th style={thStyle}>Derived section</th>
                            <th style={thStyle}>Feature</th>
                            <th style={thStyle}>Section override</th>
                            <th style={thStyle}>Column</th>
                            <th style={thStyle}>Rank</th>
                        </tr>
                    </thead>
                    <tbody>
                        {posts.map(p => {
                            const l = layouts[p.id] ?? {};
                            const busy = !!pending[p.id];
                            return (
                                <tr key={p.id} style={rowStyle(l.paper_feature)}>
                                    <td style={tdStyle}>
                                        <div style={{ fontWeight: 600 }}>
                                            <Link to={`/posts/${p.id}`} style={{ color: 'inherit' }}>
                                                {p.title || '(untitled)'}
                                            </Link>
                                        </div>
                                        <div style={{ fontSize: 11, color: '#888' }}>
                                            {p.post_type} · {timeAgo(p.created_at)}
                                            {p.topic && <> · <span className="topic-flair">{p.topic}</span></>}
                                        </div>
                                    </td>
                                    <td style={tdStyle}>{derivedSection(p.post_type)}</td>
                                    <td style={tdStyle}>
                                        <select
                                            value={l.paper_feature ?? ''}
                                            disabled={busy}
                                            onChange={e => patch(p.id, { paper_feature: (e.target.value || null) as PostLayout['paper_feature'] })}
                                        >
                                            <option value="">normal</option>
                                            <option value="hero">hero</option>
                                            <option value="hidden">hidden</option>
                                        </select>
                                    </td>
                                    <td style={tdStyle}>
                                        <input
                                            list={'sections-' + p.id}
                                            value={l.paper_section ?? ''}
                                            disabled={busy}
                                            placeholder="(derived)"
                                            style={{ width: 160 }}
                                            onChange={e => setLayouts(m => ({
                                                ...m, [p.id]: { ...(m[p.id] ?? {}), paper_section: e.target.value },
                                            }))}
                                            onBlur={e => patch(p.id, { paper_section: e.target.value || null })}
                                        />
                                        <datalist id={'sections-' + p.id}>
                                            {SECTION_PRESETS.map(s => <option key={s} value={s} />)}
                                        </datalist>
                                    </td>
                                    <td style={tdStyle}>
                                        <select
                                            value={l.paper_column ?? ''}
                                            disabled={busy}
                                            onChange={e => patch(p.id, { paper_column: (e.target.value || null) as PostLayout['paper_column'] })}
                                        >
                                            <option value="">full</option>
                                            <option value="left">left</option>
                                            <option value="right">right</option>
                                        </select>
                                    </td>
                                    <td style={tdStyle}>
                                        <input
                                            type="number"
                                            value={l.paper_rank ?? ''}
                                            disabled={busy}
                                            style={{ width: 60 }}
                                            onChange={e => setLayouts(m => ({
                                                ...m, [p.id]: {
                                                    ...(m[p.id] ?? {}),
                                                    paper_rank: e.target.value === '' ? null : Number(e.target.value),
                                                },
                                            }))}
                                            onBlur={e => patch(p.id, {
                                                paper_rank: e.target.value === '' ? null : Number(e.target.value),
                                            })}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </main>
    );
}

function derivedSection(postType: string): string {
    switch (postType) {
        case 'offer':  return 'Tonight';
        case 'event':  return 'This Week';
        case 'notice': return 'Notices';
        default:       return 'Around the Suburb';
    }
}

const navStyle: React.CSSProperties = {
    display: 'flex', gap: 12, padding: '8px 0', marginBottom: 12,
    borderBottom: '1px solid #cfc9b7',
    fontFamily: '-apple-system, sans-serif',
    fontSize: 12, textTransform: 'uppercase', letterSpacing: 1,
    alignItems: 'center',
};
const navLink: React.CSSProperties = { color: '#333', textDecoration: 'none', padding: '4px 10px' };
const tableStyle: React.CSSProperties = {
    width: '100%', borderCollapse: 'collapse', fontSize: 13,
    background: '#faf8f2', border: '1px solid #cfc9b7',
};
const thStyle: React.CSSProperties = {
    textAlign: 'left', padding: '8px 10px', background: '#f2eee0',
    borderBottom: '1px solid #cfc9b7', fontSize: 11,
    textTransform: 'uppercase', letterSpacing: 1, color: '#666',
};
const tdStyle: React.CSSProperties = {
    padding: '8px 10px', borderBottom: '1px solid #e5e0cf', verticalAlign: 'top',
};
function rowStyle(feature: PostLayout['paper_feature']): React.CSSProperties {
    if (feature === 'hero')   return { background: '#fdf6d9' };
    if (feature === 'hidden') return { background: '#f5e5e5', opacity: 0.6 };
    return {};
}
